import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import DoneIcon from "@mui/icons-material/Done";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PhotoIcon from "@mui/icons-material/Photo";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  DISCLAIMER_INFO_HIGHLIGHT_URL,
  DISCLAIMER_WARNING_HIGHLIGHT_URL,
} from "pages/commons/constants";
import { type Dispatch, type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { logCustomEvent } from "../../datadog";
import { getAuthenticatedAxios } from "../../hooks/authentication";
import type {
  DigitalSample,
  DigitalSampleAsset,
} from "../../hooks/digital-samples";
import { downloadAsset } from "../../service/download-asset";
import {
  buildDigitalSampleTrackingData,
  inferAssetType,
  trackDownload,
} from "../../service/tracking";
// import {DocumentationLink} from "../documentationLink/DocumentationLink";
import { AxFDownloadDialog } from "./AxFDownloadDialog";

/**
 * Props for {@link DigitalSampleDownloadDialog}.
 */
export interface DigitalSampleDownloadDialogProps {
  /** Controls dialog visibility. */
  open: boolean;
  /** Called when the user dismisses the dialog. */
  onClose: Dispatch<any>;
  /** DSM digital sample whose compliant assets are listed for download. */
  digitalSample: DigitalSample;
  /** Colored material the sample belongs to — used for tracking metadata. */
  material: ColoredMaterial;
}

/**
 * Lists the compliant assets of a DSM digital sample (images, renderings,
 * AxF files) and lets the user download one. AxF selections delegate to
 * {@link AxFDownloadDialog}; all other asset types download inline.
 *
 * On a non-AxF download click this fires fire-and-forget `trackDownload`
 * plus a `digital-sample-download` RUM action whose `assetType` is derived
 * from the raw asset type via `inferAssetType`. Tracking runs on user
 * intent (before the fetch) so cache hits, CORS/preflight failures, or
 * expired pre-signed URLs don't silently drop events. AxF downloads are
 * tracked by `AxFDownloadDialog`.
 */
export const DigitalSampleDownloadDialog: FC<
  DigitalSampleDownloadDialogProps
> = ({ open, onClose, digitalSample, material }) => {
  const [fileToAcknowledge, setFileToAcknowledge] =
    useState<DigitalSampleAsset>();
  const { t } = useTranslation();

  const [targetId, setTargetId] = useState<string>("");
  const [downloadState, setDownloadState] = useState<string>("");

  const downloads = (digitalSample?.assets ?? []).filter((a) =>
    a.tags.includes("COMPLIANT"),
  );

  // const documentationLink = <DocumentationLink document="digitalSampleSharing" label="legal requirements"/>;
  const onDownload = (asset: DigitalSampleAsset) => {
    if (asset.type.includes("axf")) {
      setFileToAcknowledge(asset);
      return;
    }
    const { axios } = getAuthenticatedAxios();
    trackDownload(axios, {
      assetType: inferAssetType(asset.type),
      digitalSample: buildDigitalSampleTrackingData(material, digitalSample),
      asset: {
        id: asset.id,
        fileName: asset.fileName,
        type: asset.type,
        tags: asset.tags,
      },
    });
    const colorCode =
      material.color.finalColorCode ?? material.color.preliminaryColorCode;
    logCustomEvent("digital-sample-download", {
      action: "download",
      digital_sample_id: digitalSample.id,
      digital_sample_asset_type: asset.type,
      material_grade: material.material.shortGrade,
      material_color_code: colorCode,
    });
    downloadAsset(asset, setTargetId, setDownloadState);
  };

  const renderDownloadState = (
    stateString: string,
    Id: string,
    currentID: string,
  ) => {
    let color = "";
    let resultText = "";
    if (stateString === "DOWNLOADING") {
      color = "#4D4D4D";
      resultText = "Downloading...";
    } else if (stateString === "DOWNLOADED") {
      color = "#03BB7E";
      resultText = "Downloaded";
    } else if (stateString === "DOWNLOAD_FAILED") {
      color = "#E50101";
      resultText = "Download failed due to a network error. Please try again.";
    }
    return (
      <Typography
        component="span"
        sx={{
          color: `${color}`,
          display: "inline",
          fontSize: "var(--body-b3-12-regular-size, 12px)",
        }}
      >
        {currentID === Id ? (
          <>
            {resultText}{" "}
            {stateString === "DOWNLOADED" ? (
              <DoneIcon sx={{ fontSize: "12px" }} />
            ) : (
              ""
            )}
          </>
        ) : (
          ""
        )}
      </Typography>
    );
  };

  const handleDisclaimerWarningHighlight = () => {
    window.open(DISCLAIMER_WARNING_HIGHLIGHT_URL);
  };

  const handleDisclaimerInfoHighlight = () => {
    window.open(DISCLAIMER_INFO_HIGHLIGHT_URL);
  };
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth={"md"}>
        <DialogTitle>Downloads</DialogTitle>
        <DialogContent>
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              padding: "16px",
              "> .MuiAlert-message": { padding: 0 },
              fontSize: "var(--body-b2-14-regular-size, 14px)",
            }}
          >
            {t("axf_disclaimer_warning_firstPart")}
            <Typography
              onClick={handleDisclaimerWarningHighlight}
              variant="label"
              component={"span"}
              sx={{ color: "#009FE4", "&:hover": { cursor: "pointer" } }}
            >
              {t("axf_disclaimer_warning_highlight")}
            </Typography>
            {t("axf_disclaimer_warning_secondPart")}
          </Alert>
          <Alert
            severity="info"
            sx={{
              mb: 2,
              padding: "16px",
              color: "#0371A1",
              "> .MuiAlert-message": { padding: 0 },
              fontSize: "var(--body-b2-14-regular-size, 14px)",
            }}
          >
            {t("axf_disclaimer_info")}
            <Typography
              onClick={handleDisclaimerInfoHighlight}
              sx={{
                color: "#009FE4",
                fontWeight: 500,
                paddingTop: "8px",
                fontSize: "var(--body-b2-14-regular-size, 14px)",
                "&:hover": { cursor: "pointer" },
              }}
            >
              {t("axf_disclaimer_info_highlight")}
            </Typography>
          </Alert>
          <DialogContentText>
            The following files are available to download:
          </DialogContentText>
          <List dense>
            {downloads.map((asset) => (
              <ListItem
                key={asset.assetUrl}
                secondaryAction={
                  <Button
                    endIcon={<DownloadIcon />}
                    title={`Download ${asset.fileName}`}
                    color="primary"
                    onClick={() => onDownload(asset)}
                  >
                    Download
                  </Button>
                }
              >
                <ListItemIcon>
                  {asset.type.includes("image") ? (
                    <PhotoIcon />
                  ) : (
                    <InsertDriveFileIcon />
                  )}
                </ListItemIcon>
                <ListItemText
                  sx={{
                    ">span.MuiTypography-root": {
                      width: "80%",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    },
                  }}
                  primary={asset.fileName}
                  secondary={
                    <Typography
                      component="span"
                      sx={{ color: "text.secondary", display: "inline" }}
                    >
                      {asset.type}
                      <br />
                      {renderDownloadState(downloadState, asset.id, targetId)}
                    </Typography>
                  }
                ></ListItemText>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <AxFDownloadDialog
        open={fileToAcknowledge !== undefined}
        onClose={() => {
          setFileToAcknowledge(undefined);
        }}
        axfFile={fileToAcknowledge}
        material={material}
        digitalSample={digitalSample}
        onChangeId={setTargetId}
        onDownloadState={setDownloadState}
      />
    </>
  );
};
